<template>
  <div class="app-container">
    <!-- Header -->
    <div class="hidden shrink-0 pt-8 pb-4 px-6 flex justify-between items-center z-10" id="nav-header">
      <button class="p-2 -ml-2 text-white/50 hover:text-white" @click="prevPage">
        <span class="iconify text-2xl" data-icon="hugeicons:arrow-left-01"></span>
      </button>
      <div class="h-1 w-24 bg-white/10 rounded-full overflow-hidden">
        <div class="h-full bg-white transition-all duration-500" id="progress-bar"></div>
      </div>
      <div class="w-10"></div>
    </div>

    <!-- 1. Hero Page -->
    <div class="page" :class="{ active: currentPage === 'hero' }">
      <div class="relative flex flex-col p-0 min-h-full">
        <!-- 背景图片 -->
        <div class="absolute inset-0">
          <img alt="Smart Home Concept" class="w-full h-full object-cover" src="@/assets/picture/background.jpg">
          <div class="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
        </div>
        
        <!-- 内容容器，确保按钮始终在底部 -->
        <div class="relative flex flex-col flex-1 justify-end pb-32 p-10 space-y-4">
          <div class="w-12 h-1 bg-white opacity-40 rounded-full mb-2"></div>
          <h1 class="text-4xl font-semibold tracking-tight leading-tight">
            帮您设计<br/>未来的家。
          </h1>
          <p class="text-white/60 text-lg">仅需两分钟即可帮您定制全屋智能。<br><br><br></p>
          
          <!-- 按钮容器，确保按钮间距和位置 -->
          <div class="flex flex-col space-y-4 mt-36">
            <button class="w-full bg-white text-black font-medium py-4 rounded-full text-lg active:scale-95 transition-transform" @click="nextPage('signal-problem')">
              开始定向设计
            </button>
            <!-- <button class="w-full bg-white text-black font-medium py-4 rounded-full text-lg active:scale-95 transition-transform" @click="nextPage('my-order')">
              查询我的设计
            </button> -->
            <!-- <button class="w-full bg-white text-black font-medium py-4 rounded-full text-lg active:scale-95 transition-transform" @click="nextPage('ai-qa')">
              智小家
            </button> -->
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Signal Problem Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'signal-problem' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家是否有信号覆盖问题？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们确定后续的方案方向</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in signalProblemOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedSignalProblem === option.value }"
            @click="selectSignalProblem(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedSignalProblem !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedSignalProblem ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedSignalProblem"
          @click="handleSignalProblemNext"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 7. House Type Selection (Updated) -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'house-type' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家是几室几厅？</h2>
        <p class="text-white/40 mb-2 italic">这将用于分析您家的网络情况和智能方案的基础配置</p>
        <div class="space-y-6">
        <div 
          v-for="(option, index) in houseTypeOptions" 
          :key="index"
          class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto"
          :class="{ selected: selectedHouseType === option }"
          @click="selectHouseType(option)"
        >
          <span>{{ option.name }}</span>
          <span class="iconify" :class="{ 'opacity-20': selectedHouseType !== option }" data-icon="hugeicons:tick-02"></span>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedHouseType ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedHouseType"
          @click="nextPage('signal-weak-area')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 6. Signal Weak Area Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'signal-weak-area' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">哪些房间信号较差？(多选)</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们针对性地优化信号覆盖</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in signalWeakAreaOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedSignalWeakArea.includes(option.value) }"
            @click="selectSignalWeakArea(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': !selectedSignalWeakArea.includes(option.value) }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedSignalWeakArea.length > 0 ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="selectedSignalWeakArea.length === 0"
          @click="nextPage('decoration-status')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 7. Decoration Status Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'decoration-status' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家是毛坯房还是已装修？</h2>
        <p class="text-white/40 mb-2 italic">这将影响我们为您推荐的网络方案类型</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in decorationStatusOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedDecorationStatus === option.value }"
            @click="selectDecorationStatus(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedDecorationStatus !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedDecorationStatus ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedDecorationStatus"
          @click="nextPage('prewiring-status')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 8. Prewiring Status Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'prewiring-status' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">{{ getPrewiringQuestion() }}</h2>
        <p class="text-white/40 mb-2 italic">这是决定FTTR/AC+AP能否部署的核心前提，直接影响方案推荐方向</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in getAvailablePrewiredOptions()" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedPrewiredStatus === option.value }"
            @click="selectPrewiredStatus(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedPrewiredStatus !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedPrewiredStatus ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedPrewiredStatus"
          @click="nextPage('broadband-info')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 4. Broadband Info Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'broadband-info' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家宽带是哪家运营商？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们了解您的网络基础情况</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in broadbandInfoOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedBroadbandInfo === option.value }"
            @click="selectBroadbandInfo(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedBroadbandInfo !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedBroadbandInfo ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedBroadbandInfo"
          @click="selectedBroadbandInfo === 'not-installed' ? nextPage('network-solution') : nextPage('network-speed')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 5. Network Speed Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'network-speed' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家宽带网速有多少？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们了解您的网络基础情况</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in networkSpeedOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedNetworkSpeed === option.value }"
            @click="selectNetworkSpeed(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedNetworkSpeed !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedNetworkSpeed ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedNetworkSpeed"
          @click="nextPage('network-type')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 6. Network Type Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'network-type' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您家目前组网方式是哪种？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们了解您的网络基础情况</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in networkTypeOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedNetworkType === option.value }"
            @click="selectNetworkType(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedNetworkType !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedNetworkType ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedNetworkType"
          @click="selectedNetworkType === 'fttr' || selectedNetworkType === 'acap' ? nextPage('smart-device-category') : nextPage('network-solution')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 8. Network Solution Page (Updated) -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'network-solution' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">组网方式选择</h2>
        <p class="text-white/40 mb-2 italic">优秀的组网方式能提供更稳定、更快速的网络连接，满足您的网络需求</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <!-- AC+AP 方案 -->
          <div 
            class="option-card glass-card p-5 cursor-pointer"
            :class="{ selected: selectedNetworkSolution === 'acap' }"
            @click="selectNetworkSolution('acap')"
          >
            <h3 
              class="text-xl font-medium mb-4"
              :class="selectedNetworkSolution === 'acap' ? 'text-black' : ''"
            >AC+AP分布式组网(多数人选择)</h3>
            <ul class="space-y-2">
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : 'text-white/80'"
                ><strong>集中管控：</strong>AC 统一管理所有 AP，漫游切换顺畅。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : 'text-white/80'"
                ><strong>PoE 供电：</strong>网线供电，部署灵活，适合网络需求高的用户。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : 'text-white/80'"
                ><strong>兼容性好：</strong>兼容现有网线，改造方便。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'acap' ? 'text-black' : 'text-white/80'"
                ><strong>更加美观：</strong>且可与墙面面板统一配色。</span>
              </li>
            </ul>
          </div>
          
          <!-- FTTR 方案 -->
          <div 
            class="option-card glass-card p-5 cursor-pointer"
            :class="{ selected: selectedNetworkSolution === 'fttr' }"
            @click="selectNetworkSolution('fttr')"
          >
            <h3 
              class="text-xl font-medium mb-4"
              :class="selectedNetworkSolution === 'fttr' ? 'text-black' : ''"
            >FTTR光纤到房间(未来流行的组网方式)</h3>
            <ul class="space-y-2">
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : 'text-white/80'"
                ><strong>超高速率：</strong>光纤传输，全屋可跑满千兆 / 万兆。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : 'text-white/80'"
                ><strong>抗干扰强：</strong>不受电磁干扰，信号极稳。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : 'text-white/80'"
                ><strong>低时延：</strong>漫游时延 &lt; 20ms，适合电竞 / 直播。</span>
              </li>
              <li class="flex items-start">
                <span 
                  class="mr-2"
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : ''"
                >•</span>
                <span 
                  :class="selectedNetworkSolution === 'fttr' ? 'text-black' : 'text-white/80'"
                ><strong>寿命长：</strong>光纤可用 20 年 +，带宽潜力无限。</span>
              </li>
            </ul>
          </div>
          
          <!-- 暂时没考虑好选项 -->
          <div 
            class="option-card glass-card p-5 cursor-pointer"
            :class="{ selected: selectedNetworkSolution === 'none' }"
            @click="selectNetworkSolution('none')"
          >
            <span class="font-medium">暂时没考虑好</span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedNetworkSolution ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedNetworkSolution"
          @click="nextPage('smart-device-category')"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 9. Smart Device Interest Page -->
    <!-- <div class="page flex flex-col h-full" :class="{ active: currentPage === 'smart-device-interest' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">除了信号覆盖，您是否还关注其他智能设备？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们为您提供更全面的智能方案</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in smartDeviceInterestOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex justify-between items-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedSmartDeviceInterest === option.value }"
            @click="selectSmartDeviceInterest(option.value)"
          >
            <span>{{ option.label }}</span>
            <span class="iconify" :class="{ 'opacity-20': selectedSmartDeviceInterest !== option.value }" data-icon="hugeicons:tick-02"></span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedSmartDeviceInterest ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="!selectedSmartDeviceInterest"
          @click="handleSmartDeviceInterestNext"
        >
          下一步
        </button>
      </div>
    </div> -->

    <!-- 10. Smart Device Category Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'smart-device-category' }">
      <div class="flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">您最想先体验哪类智能设备？</h2>
        <p class="text-white/40 mb-2 italic">这将帮助我们优先为您展示相关产品</p>
        <div class="space-y-6">
        <div class="space-y-4">
          <div 
            v-for="option in smartDeviceCategoryOptions" 
            :key="option.value"
            class="option-card glass-card p-5 flex flex-col items-start justify-center max-w-[96%] mx-auto cursor-pointer"
            :class="{ selected: selectedSmartDeviceCategory.includes(option.value) }"
            @click="selectSmartDeviceCategory(option.value)"
          >
            <div class="flex justify-between items-center w-full">
              <span class="text-lg font-medium" :class="selectedSmartDeviceCategory.includes(option.value) ? 'text-black' : 'text-white'">{{ option.label }}</span>
              <span class="iconify" :class="{ 'opacity-20': !selectedSmartDeviceCategory.includes(option.value) }" data-icon="hugeicons:tick-02"></span>
            </div>
            <span class="text-sm mt-2" :class="selectedSmartDeviceCategory.includes(option.value) ? 'text-black' : 'text-white/70'">{{ option.description }}</span>
          </div>
        </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
          :class="selectedSmartDeviceCategory.length > 0 ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
          :disabled="selectedSmartDeviceCategory.length === 0"
          @click="handleSmartDeviceCategoryNext"
        >
          下一步
        </button>
      </div>
    </div>

    <!-- 6. Device Selection -->
    <div class="page" :class="{ active: currentPage === 'devices', 'flex flex-col h-full': currentPage === 'devices' }">
      <!-- 可滚动的内容区域 -->
      <div v-if="currentPage === 'devices'" class="flex-1 overflow-y-auto overflow-x-hidden space-y-6">
        <!-- 设备类型信息 -->
        <div v-if="filteredDeviceOptions[currentDeviceIndex]">
          <!-- 设备类型标题和图标 -->
          <div class="flex items-center space-x-4">
            <img 
              :src="filteredDeviceOptions[currentDeviceIndex].icon" 
              class="w-9 h-9" 
              :style="{ filter: `drop-shadow(0 0 2px ${getDeviceIconColor(filteredDeviceOptions[currentDeviceIndex].name)})` }"
            />
            <h2 class="text-2xl font-medium mt-0 mb-2">{{ filteredDeviceOptions[currentDeviceIndex].name }}</h2>
          </div>
          <p class="text-white/40 mb-2 italic">{{ filteredDeviceOptions[currentDeviceIndex].description }}</p>
          <!-- 设备视频或图片 -->
          <div class="glass-card p-3 relative">
            <div 
              class="relative overflow-hidden rounded-lg"
              style="aspect-ratio: 16 / 9;"
            >
              <!-- 优先展示视频 -->
              <video 
                v-if="filteredDeviceOptions[currentDeviceIndex].video"
                :src="filteredDeviceOptions[currentDeviceIndex].video" 
                class="w-full h-full object-cover"
                :alt="filteredDeviceOptions[currentDeviceIndex].name + '视频'"
                autoplay
                muted
                loop
                playsinline
              >
              </video>
              <!-- 没有视频时展示图片 -->
              <img 
                v-else-if="filteredDeviceOptions[currentDeviceIndex].pictures && filteredDeviceOptions[currentDeviceIndex].pictures.length > 0"
                :src="filteredDeviceOptions[currentDeviceIndex].pictures[0]" 
                class="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                :alt="filteredDeviceOptions[currentDeviceIndex].name + '图片'"
                @click="openCarouselModal(filteredDeviceOptions[currentDeviceIndex].pictures[0])"
              >
            </div>
          </div>
          
          <!-- 设备描述 -->
          <!-- <p class="text-white/60 text-left whitespace-pre-line">{{ filteredDeviceOptions[currentDeviceIndex].description }}</p> -->
          
          <!-- 对应设备类型的设备列表 -->
          <div class="space-y-4">
            <!-- <h3 class="text-lg font-medium text-white/80">推荐设备</h3> -->
            <p class="text-white/40 mb-2 italic">点击产品图片查看详情</p>
            <div class="space-y-3">
              <!-- 设备列表 -->
              <div 
                v-for="device in filteredDevicesByCategory" 
                :key="device.id"
                class="option-card glass-card p-5 flex justify-between items-center cursor-pointer"
                :class="{ selected: selectedDevices.includes(device.id) }"
                @click="toggleDeviceSelection(device)"
              >
                <div class="flex items-center space-x-4 flex-1">
                  <!-- 点击头像展示详情页 -->
                  <div 
                    class="cursor-pointer"
                    @click.stop="showDeviceInfo(device)"
                  >
                    <img alt="Product" class="w-9 h-9 rounded-lg" :src="device.picture">
                  </div>
                  <div class="flex-1">
                    <!-- 点击名称展示详情页 -->
                    <div
                      class="font-medium text-sm cursor-pointer inline-block"
                      :class="selectedDevices.includes(device.id) ? 'text-black' : ''"
                      @click.stop="showDeviceInfo(device)"
                    >{{ device.name }}<span class="ml-1" :class="selectedDevices.includes(device.id) ? 'text-black/60' : 'text-white/60'">?</span></div>
                    <!-- <div 
                      class="text-xs"
                      :class="selectedDevices.includes(device.id) ? 'text-black' : 'text-white/40'"
                    >{{ device.model }}</div> -->
                    <!-- 去掉价格显示 -->
                    <!-- 描述文字，不做点击事件，选中后颜色变化 -->
                    <!-- <div 
                      class="text-xs mt-1"
                      :class="selectedDevices.includes(device.id) ? 'text-black' : 'text-white/60'"
                    >{{ device.shortDescription }}</div> -->
                  </div>
                </div>
                <!-- 选中图标 -->
                <span class="iconify text-xl" :class="{ 'opacity-20': !selectedDevices.includes(device.id) }" 
                  data-icon="hugeicons:tick-02">
                </span>
              </div>
              
              <!-- 我不需要选项 - 移到最下面 -->
              <div
                class="option-card glass-card p-5 flex justify-between items-center mt-2"
                :class="{
                  'selected': needlessDevices.includes(currentCategory),
                  'cursor-pointer': !(currentCategory === '全屋控制' && categoryHasSelectedDevices),
                  'cursor-not-allowed opacity-50': currentCategory === '全屋控制' && categoryHasSelectedDevices
                }"
                @click="(currentCategory === '全屋控制' && categoryHasSelectedDevices) ? null : toggleNeedless(currentCategory)"
              >
                <span class="font-medium">我不需要</span>
                <span class="iconify text-xl" :class="{ 'opacity-20': !needlessDevices.includes(currentCategory) }" data-icon="hugeicons:tick-02"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 固定在底部的导航按钮 -->
      <div v-if="currentPage === 'devices' && filteredDeviceOptions[currentDeviceIndex]" class="pt-4 border-t border-white/10 pb-16 mt-auto">
        <div class="flex space-x-4">
          <button 
            class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
            @click="prevPage"
          >
            上一步
          </button>
          <button 
            class="flex-1 font-medium py-4 rounded-full text-lg transition-all"
            :class="canProceedToNext ? 'bg-white text-black' : 'bg-white/10 text-white opacity-50 cursor-not-allowed'"
            :disabled="!canProceedToNext"
            @click="nextDevice"
          >
            {{ currentDeviceIndex === filteredDeviceOptions.length - 1 ? '生成定制方案' : '下一步' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 5. Solution Page -->
    <div class="page flex flex-col h-full" :class="{ active: currentPage === 'solution' }">
      <!-- 固定在顶部的标题和预估金额 -->
      <div class="sticky top-0 z-10 bg-black bg-opacity-80 backdrop-blur-md pb-4">
        <h2 class="text-2xl font-medium mt-0 mb-2">为您定制的方案</h2>
        <p class="text-white/40 mb-2 italic">智小家觉得这个方案已经很全面了，让我们先尝试一下</p>
        <!-- <div class="bg-gradient-to-br from-blue-600 to-purple-700 p-5 rounded-3xl my-6">
          <div class="text-white/60 text-sm">预估总价 (含上门安装)</div>
          <div class="text-4xl font-bold mt-1">¥ {{ estimatedPriceRange.min }} - {{ estimatedPriceRange.max }} <span class="text-lg font-normal opacity-70"></span></div>
        </div> -->
      </div>
      
      <!-- AI分析框 -->
      <div class="p-4 glass-card mx-4 mb-6">
        <h3 class="text-lg font-medium mb-4">分析一下您的方案，看看效果怎样</h3>
        <div v-if="aiAnalysis.loading" class="flex items-center justify-center py-8">
          <div class="flex flex-col items-center">
            <div class="w-12 h-12 border-4 border-t-blue-500 border-white/20 rounded-full animate-spin mb-4"></div>
            <span class="text-white/60">正在分析您的方案...</span>
          </div>
        </div>
        <div v-else-if="aiAnalysis.error" class="text-red-400 py-4">
          {{ aiAnalysis.error }}
        </div>
        <div v-else-if="aiAnalysis.content" class="text-white/80 leading-relaxed py-2">
          {{ aiAnalysis.content }}
        </div>
        <div v-else class="text-white/40 italic py-2">
          点击下方按钮获取AI方案分析
        </div>
        <button 
          class="mt-4 bg-blue-600 text-white font-medium py-3 px-6 rounded-full text-sm transition-all"
          @click="getAiAnalysis"
          :disabled="aiAnalysis.loading"
        >
          {{ aiAnalysis.loading ? '分析中...' : '开始分析' }}
        </button>
      </div>
      
      <!-- 可滚动的设备列表区域 -->
      <div class="flex-1 space-y-6 overflow-y-auto pr-2">
        <!-- 按设备类型分组展示 -->
        <div v-for="(devices, category) in groupedDevices" :key="category" class="space-y-4">
          <!-- 分类标题 -->
          <!-- <h3 class="text-lg font-medium text-white/80">{{ category }}</h3> -->
          <!-- 该分类下的设备列表 -->
          <div class="space-y-3">
            <div 
              v-for="(device, index) in devices" 
              :key="index"
              class="flex items-center p-4 glass-card"
            >
              <div 
                class="flex items-center space-x-4 cursor-pointer flex-1"
                @click="showDeviceInfo(device)"
              >
                <img alt="Product" class="w-9 h-9 rounded-lg" :src="device.picture">
                <div class="font-medium text-sm">{{ device.name }}</div>
              </div>
              <!-- <div 
                class="text-xs text-white/60 mt-1 cursor-pointer"
                @click="showDeviceInfo(device)"
              >{{ device.shortDescription }}</div> -->
            </div>
          </div>
        </div>
      </div>
      
      <!-- 底部按钮组 -->
      <div class="pt-4 border-t border-white/10 pb-16 mt-auto">
        <div class="flex space-x-4">
          <button 
            class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
            @click="prevPage"
          >
            上一步
          </button>
          <button 
            class="flex-1 bg-white text-black font-medium py-4 rounded-full text-lg transition-all"
            @click="nextPage('contact')"
          >
            确认方案
          </button>
        </div>
      </div>
    </div>

    <!-- 6. Contact Form -->
    <div class="page" :class="{ active: currentPage === 'contact' }">
      <h2 class="text-2xl font-medium mt-0 mb-2">即刻定制智能家庭</h2>
      <p class="text-white/40 mb-2">留下您的联系方式，我们将为您定制智能家庭方案</p>
      <div class="space-y-6 overflow-y-auto h-[60vh] pr-2">
        <div class="space-y-2">
          <label class="text-xs uppercase tracking-widest text-white/40 ml-1">姓名 / 称呼</label>
          <input 
            v-model="contactInfo.name"
            class="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:border-white/30"
            placeholder="如：张先生"
            type="text"
          />
        </div>
        <div class="space-y-2">
          <label class="text-xs uppercase tracking-widest text-white/40 ml-1">手机号码</label>
          <input 
            v-model="contactInfo.phone"
            class="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:border-white/30"
            placeholder="用于接收详细清单"
            type="tel"
          />
          <div v-if="contactErrors.phone" class="text-red-400 text-xs ml-1">
            {{ contactErrors.phone }}
          </div>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button 
          class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg transition-all"
          @click="prevPage"
        >
          上一步
        </button>
        <button 
          class="flex-1 bg-blue-600 text-white font-medium py-4 rounded-full text-lg transition-all"
          :class="{
            'opacity-50 cursor-not-allowed': !isContactFormValid || submitLoading,
            'bg-blue-600': !submitLoading,
            'bg-blue-700': submitLoading
          }"
          :disabled="!isContactFormValid || submitLoading"
          @click="submitOrder()"
        >
          {{ submitLoading ? '提交中...' : '立即提交' }}
        </button>
      </div>
    </div>

    <!-- 7. WeChat QR Code -->
    <div class="page" :class="{ active: currentPage === 'wechat' }">
      <div class="flex-1 flex flex-col items-center pt-10">
        <div class="relative w-24 h-24 mb-2">
          <img alt="Avatar" class="w-full h-full rounded-full border-2 border-white/20" src="@/assets/picture/avatar.jpg">
          <div class="absolute bottom-0 right-0 w-6 h-6 bg-green-500 border-2 border-black rounded-full"></div>
        </div>
        <h2 class="text-xl font-medium">智能家居方案专家</h2>
        <p class="text-white/40 text-sm mt-1">已累计服务 1200+ 业主</p>
        <div class="glass-card mt-10 p-4 bg-white">
          <img alt="WeChat QR Code" class="w-48 h-48" :src="qrCodePath">
        </div>
        <div class="mt-2 text-center space-y-2">
          <p class="text-sm font-medium">长按保存二维码，微信扫一扫</p>
          <p class="text-xs text-white/40">加好友可领取更多优惠</p>
        </div>
      </div>
      <div class="flex space-x-4 mb-2">
        <button class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg" @click="nextPage('ai-qa')">
          我有疑问
        </button>
        <button class="flex-1 bg-white/10 text-white font-medium py-4 rounded-full text-lg" @click="restart">
          重新开始
        </button>
      </div>
    </div>
    
    <!-- 8. AI Q&A Page -->
    <div class="page" :class="{ active: currentPage === 'ai-qa' }" style="display: flex; flex-direction: column; height: 100%; width: 100%; padding: 0;">
      <!-- 内容容器，添加内边距 -->
      <div class="flex flex-col p-5 h-full">
        <h2 class="text-2xl font-medium mt-0 mb-2">智能问答</h2>
        <p class="text-white/40 mb-2 italic">有任何问题都可以咨询智小家！</p>
        
        <!-- 使用新的AIChat组件 -->
        <div class="flex-1 mt-4">
          <AIChat />
        </div>
      </div>
    </div>
    
    <!-- 9. Order Query Page -->
    <div class="page" :class="{ active: currentPage === 'my-order' }">
      <h2 class="text-2xl font-medium mt-0 mb-2">查询我的设计</h2>
      <p class="text-white/40 mb-2 italic">请输入您的姓名和手机号进行查询</p>
      
      <div class="space-y-6">
        <!-- 姓名输入 -->
        <div class="space-y-2">
          <label class="text-xs uppercase tracking-widest text-white/40 ml-1">姓名 / 称呼</label>
          <input 
            v-model="orderQueryForm.name"
            class="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:border-white/30"
            placeholder="如：张先生"
            type="text"
            @input="resetPagination"
          />
        </div>
        
        <!-- 手机号输入 -->
        <div class="space-y-2">
          <label class="text-xs uppercase tracking-widest text-white/40 ml-1">手机号码</label>
          <input 
            v-model="orderQueryForm.phone"
            class="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:border-white/30"
            placeholder="用于接收详细清单"
            type="tel"
            @input="resetPagination"
          />
          <div v-if="orderQueryErrors.phone" class="text-red-400 text-xs ml-1">
            {{ orderQueryErrors.phone }}
          </div>
        </div>
      </div>
      
      <!-- 查询按钮 -->
      <button 
        class="mt-2 mb-4 w-full bg-blue-600 text-white font-medium py-4 rounded-full text-lg transition-all"
        :class="isOrderQueryValid ? '' : 'opacity-50 cursor-not-allowed'"
        :disabled="!isOrderQueryValid"
        @click="queryOrder"
      >
        {{ orderQueryLoading ? '查询中...' : '查询设计' }}
      </button>
      
      <!-- 结果展示区域 -->
      <div class="space-y-4">
        <!-- 错误提示 -->
        <div v-if="orderQueryError" class="bg-red-500/20 border border-red-500/40 text-red-300 p-4 rounded-2xl">
          {{ orderQueryError }}
        </div>
        
        <!-- 加载状态 -->
        <div v-else-if="orderQueryLoading" class="flex justify-center items-center py-8">
          <div class="text-white/60">查询中...</div>
        </div>
        
        <!-- 查询结果 -->
        <div v-else-if="orderQueryResult" class="space-y-4">
          <!-- 结果数量 -->
          <div class="text-white/60 text-sm">共 {{ orderQueryResult.total }} 条设计记录</div>
          
          <!-- 订单列表 -->
          <div class="space-y-4">
            <div 
              v-for="order in orderQueryResult.records" 
              :key="order.orderNo"
              class="bg-white/5 border border-white/10 p-4 rounded-2xl hover:bg-white/10 transition-colors cursor-pointer"
              @click="getOrderDetail(order.orderNo)"
            >
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="text-white font-medium">{{ order.orderNo }}</h3>
                  <p class="text-white/40 text-sm mt-1">{{ new Date(order.createTime).toLocaleString() }}</p>
                </div>
                <div class="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full">
                  {{ order.status }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- 分页控件 -->
          <div v-if="pagination.pages > 1" class="flex justify-center items-center space-x-2 py-4">
            <button 
              class="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/15 transition-colors"
              :disabled="pagination.current === 1"
              @click="prevPageItem"
            >
              ←
            </button>
            
            <!-- 页码按钮 -->
            <div class="flex space-x-1">
              <button 
                v-for="page in pagination.pages" 
                :key="page"
                class="w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors"
                :class="{
                  'bg-white text-black': pagination.current === page,
                  'bg-white/5 border border-white/10 hover:bg-white/15': pagination.current !== page
                }"
                @click="handlePageChange(page)"
              >
                {{ page }}
              </button>
            </div>
            
            <button 
              class="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/15 transition-colors"
              :disabled="pagination.current === pagination.pages"
              @click="nextPageItem"
            >
              →
            </button>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="orderQueryResult?.records?.length === 0" class="text-white/60 text-center py-8">
          暂无设计记录
        </div>
      </div>
    </div>
    
    <!-- 12. Order Detail Page -->
    <div class="page" :class="{ active: currentPage === 'order-detail' }">
      <!-- 顶部导航栏 -->
      <div class="sticky top-0 z-10 bg-black bg-opacity-80 backdrop-blur-md pb-4 flex items-center">
        <h2 class="text-2xl font-medium mt-0 mb-2">订单详情</h2>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="orderDetailLoading" class="flex justify-center items-center py-16">
        <div class="text-white/60">加载中...</div>
      </div>
      
      <!-- 错误提示 -->
      <div v-else-if="orderDetailError" class="bg-red-500/20 border border-red-500/40 text-red-300 p-4 rounded-2xl m-4">
        {{ orderDetailError }}
      </div>
      
      <!-- 订单详情内容 -->
      <div v-else-if="orderDetail" class="space-y-6 overflow-y-auto max-h-[80vh] pr-2">
        <!-- 订单信息卡片 -->
        <div class="bg-gradient-to-br from-blue-600 to-purple-700 p-5 rounded-3xl">
          <div class="flex justify-between items-start mb-4">
            <div>
              <h3 class="text-white font-medium mb-1">订单号</h3>
              <p class="text-white text-lg">{{ orderDetail.order?.orderNo }}</p>
            </div>
            <div class="text-xs px-2 py-1 bg-white/20 text-white rounded-full">
              {{ orderDetail.order?.status }}
            </div>
          </div>
          
          <div class="space-y-2 text-white/80">
            <div class="flex justify-between">
              <span class="text-sm">客户姓名</span>
              <span class="text-sm">{{ orderDetail.order?.customerName }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm">手机号码</span>
              <span class="text-sm">{{ orderDetail.order?.customerPhone }}</span>
            </div>
            <!-- <div class="flex justify-between">
              <span class="text-sm">订单金额</span>
              <span class="text-sm">¥{{ orderDetail.order?.price }}</span>
            </div> -->
            <div class="flex justify-between">
              <span class="text-sm">创建时间</span>
              <span class="text-sm">{{ new Date(orderDetail.order?.createTime).toLocaleString() }}</span>
            </div>
            <!-- <div class="flex justify-between">
              <span class="text-sm">更新时间</span>
              <span class="text-sm">{{ new Date(orderDetail.order?.updateTime).toLocaleString() }}</span>
            </div> -->
          </div>
        </div>
        
        <!-- 日志列表 -->
        <div class="space-y-4">
          <h3 class="text-lg font-medium text-white">订单日志</h3>
          <div class="space-y-3">
            <div 
              v-for="(log, index) in orderDetail.logs" 
              :key="index"
              class="bg-white/5 border border-white/10 p-4 rounded-2xl"
            >
              <div class="flex justify-between items-start mb-2">
                <div class="font-medium text-white">{{ log.operation }}</div>
                <div class="text-xs text-white/40">{{ new Date(log.operationTime).toLocaleString() }}</div>
              </div>
              <div class="text-sm text-white/70 mb-1">{{ log.content }}</div>
              <div class="text-sm text-white/60">
                <span class="mr-4">操作人: {{ log.operatorName }}</span>
                <span v-if="log.comment">备注: {{ log.comment }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 设备列表 -->
        <div class="space-y-4">
          <h3 class="text-lg font-medium text-white">设备清单</h3>
          <div class="space-y-3">
            <div 
              v-for="device in orderDetail.devices" 
              :key="device.id"
              class="bg-white/5 border border-white/10 p-4 rounded-2xl"
            >
              <div class="flex flex-col">
                <div class="flex justify-between items-start mb-2">
                  <div>
                    <div class="font-medium text-white">{{ device.name }}</div>
                    <!-- <div class="text-xs text-white/40 mt-1">{{ device.model }}</div> -->
                  </div>
                  <!-- <div class="text-sm text-blue-400">¥{{ device.minPrice }} - {{ device.maxPrice }}</div> -->
                </div>
                
                <!-- <div class="flex justify-between items-center mt-3">
                  <div class="text-sm text-white/60">
                    <span class="mr-4">数量: x{{ device.quantity }}</span>
                    <span>位置: {{ device.location }}</span>
                  </div>
                  <div class="text-xs px-2 py-1 bg-white/20 text-white/60 rounded-full">
                    {{ device.category }}
                  </div>
                </div> -->
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-else class="text-white/60 text-center py-16">
        暂无订单详情
      </div>
    </div>
    
    <!-- 页脚信息 -->
    <div class="text-center text-white/40 text-xs py-4 mt-auto">
      <br>泛家D©2026
    </div>
    
    <!-- 设备详情弹窗 -->
    <div 
      v-if="showDeviceModal" 
      :class="['fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4', { 'fade-out': isClosing }]"
      @click="closeDeviceModal"
    >
      <div 
        :class="['relative bg-black/40 backdrop-blur-lg rounded-2xl p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto', { 'slide-down': isClosing }]"
        @click.stop
      >
        <!-- 关闭按钮 -->
        <button class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors font-medium" @click="closeDeviceModal">
          ✕
        </button>
        
        <!-- 设备详情内容 -->
        <div v-if="selectedDevice" class="space-y-6">
          <!-- 设备大图 -->
          <div class="flex justify-center">
            <img 
              :src="selectedDevice.detailPicture" 
              alt="Device Detail" 
              class="w-full max-w-lg rounded-xl cursor-pointer"
              @click="closeDeviceModal"
            >
          </div>
          
          <!-- 设备信息 -->
          <div class="space-y-4">
            <h3 class="text-2xl font-medium">{{ selectedDevice.name }}</h3>
            <div class="text-white/60 text-sm">
              <div>{{ selectedDevice.model }}</div>
              <div>{{ selectedDevice.category }}</div>
            </div>
            
            <!-- 设备描述 -->
            <div class="pt-4 border-t border-white/10">
              <h4 class="text-lg font-medium mb-2">产品描述</h4>
              <p class="text-white/70 leading-relaxed">{{ selectedDevice.shortDescription }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 轮播图放大弹窗 -->
    <div 
      v-if="showCarouselModal" 
      :class="['fixed inset-0 bg-black z-50 flex items-center justify-center', { 'fade-out': isClosing }]"
      @click="closeCarouselModal"
    >
      <!-- 控制按钮 -->
      <div class="absolute top-6 right-6 flex space-x-3 z-10" @click.stop>
        <!-- 缩小按钮 -->
        <button 
          class="w-8 h-8 rounded-full bg-white/80 text-black flex items-center justify-center hover:bg-white transition-all duration-300 font-bold shadow-lg"
          @click="zoomOut"
          title="缩小"
        >
          −
        </button>
        <!-- 重置缩放按钮 -->
        <button 
          class="w-8 h-8 rounded-full bg-white/80 text-black flex items-center justify-center hover:bg-white transition-all duration-300 font-bold shadow-lg"
          @click="resetZoom"
          title="重置"
        >
          1:1
        </button>
        <!-- 放大按钮 -->
        <button 
          class="w-8 h-8 rounded-full bg-white/80 text-black flex items-center justify-center hover:bg-white transition-all duration-300 font-bold shadow-lg"
          @click="zoomIn"
          title="放大"
        >
          +
        </button>
        <!-- 关闭按钮 -->
        <button class="w-8 h-8 rounded-full bg-white/80 text-black flex items-center justify-center hover:bg-white transition-all duration-300 font-bold shadow-lg" @click="closeCarouselModal" title="关闭">
          ✕
        </button>
      </div>
      
      <!-- 放大后的图片 -->
      <div 
        v-if="currentCarouselImage" 
        class="w-full h-full overflow-auto"
        @wheel="handleWheel"
        style="touch-action: none;"
      >
        <div 
          class="relative w-full h-full flex items-center justify-center"
          :class="{
            'cursor-move': carouselImageScale > 1,
            'cursor-pointer': carouselImageScale <= 1,
            'slide-down': isClosing
          }"
          :style="{
            transform: `scale(${carouselImageScale}) translate(${translateX}px, ${translateY}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease, opacity 0.3s ease-out'
          }"
          @mousedown="startDrag"
          @touchstart="startDrag"
          @mouseup="endDrag"
          @touchend="endDrag"
          @click="carouselImageScale <= 1 ? closeCarouselModal : null"
        >
          <img 
            :src="currentCarouselImage" 
            alt="Carousel Image" 
            class="w-full h-full object-contain"
            style="border-radius: 0;"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import AIChat from './components/AIChat.vue';
import config from './config';

const API_BASE_URL = config.API_BASE_URL;

export default {
  name: 'App',
  components: {
    AIChat
  },
  data() {
    return {
      currentPage: 'hero',
      previousPage: null,
      // 从URL获取的客户区域参数
      area: '',
      // 从URL获取的接单人参数
      assignee: '',
      // 有效的区域列表
      validAreas: ['高淳', '鼓楼', '化工园', '建邺', '江宁', '溧水', '六合', '浦口', '栖霞', '秦淮', '玄武', '雨花'],
      // 页面访问路径数组，记录用户实际访问的页面顺序
      pageHistory: ['hero'],
      pages: ['hero', 'signal-problem', 'house-type', 'signal-weak-area', 'decoration-status', 'prewiring-status', 'broadband-info', 'network-speed', 'network-type', 'network-solution', 'smart-device-interest', 'smart-device-category', 'devices', 'solution', 'contact', 'wechat', 'ai-qa', 'my-order', 'order-detail'],
      // 用户选择情况统一存储
      userSelections: {
        houseType: null,
        broadband: null,
        wifiCoverage: null,
        networkSolution: null,
        signalProblem: null,
        wifiSolveWill: null,
        broadbandInfo: null,
        networkSpeed: null,
        networkType: null,
        signalWeakArea: null,
        decorationStatus: null,
        prewiringStatus: null,
        smartDeviceInterest: null,
        smartDeviceCategory: null,
        networkUpgradeNeed: null
      },
      // AI分析相关状态
      aiAnalysis: {
        loading: false,
        content: '',
        error: null
      },
      // AI问答相关
      aiMessages: [],
      aiInput: '',
      // 订单查询表单
      orderQueryForm: {
        name: '',
        phone: ''
      },
      // 订单查询表单错误
      orderQueryErrors: {
        phone: ''
      },
      // 订单查询结果
      orderQueryResult: null,
      orderQueryLoading: false,
      orderQueryError: '',
      // 分页相关
      pagination: {
        current: 1,
        size: 10,
        total: 0,
        pages: 0
      },
      // 订单详情相关
      orderDetail: null,
      orderDetailLoading: false,
      orderDetailError: '',
      selectedOrderNo: null,
      // 设备详情弹窗相关
      showDeviceModal: false,
      isClosing: false,
      selectedDevice: null,
      selectedDeviceTimer: null,
      // 设备库
      deviceLibrary: {},
      houseTypeOptions: [
        { id: '1', name: '一室一厅', description: '适合单身贵族或年轻情侣的小户型智能方案', devices: [] },
        { id: '2', name: '二室一厅', description: '适合小家庭或需要独立办公空间的智能方案', devices: [] },
        { id: '3', name: '三室一厅', description: '适合三口之家或多成员家庭的全面智能方案', devices: [] },
        { id: '4', name: '跃层', description: '适合复式结构住宅的双层智能解决方案', devices: [] },
        { id: '5', name: '别墅', description: '适合大户型别墅的高端全屋智能定制方案', devices: [] }
      ],
      deviceOptions: [
        { name: '智能门锁', icon: require('@/assets/ico/智能门锁.svg'), pictures :[require('@/assets/picture/deviceOptions/智能门锁1.gif')], description: '是否需要智能门锁，这样出门再也不用担心忘带钥匙啦！', isSingle: true },
        { name: '智能开关', icon: require('@/assets/ico/智能开关.svg'), pictures :[require('@/assets/picture/deviceOptions/智能开关1.gif')], description: '睡前不想下床关灯——喊一声就行，出门忘关灯——手机远程关闭!', isSingle: true },
        { name: '智能遮阳', icon: require('@/assets/ico/智能遮阳.svg'), pictures :[require('@/assets/picture/deviceOptions/智能遮阳1.gif')], description: '喊一句或者按一下开关就能轻松实现窗帘的开合！', isSingle: true },
        { name: '智能晾衣', icon: require('@/assets/ico/智能晾衣.svg'), pictures :[require('@/assets/picture/deviceOptions/智能晾衣1.gif')], description: '支持语音升降，按键升降，烘干杀菌！', isSingle: true },
        { name: '全屋控制', icon: require('@/assets/ico/全屋控制.svg'), pictures :[require('@/assets/picture/deviceOptions/全屋控制1.jpg')], description: '智能控制您的智慧家庭！', isSingle: false }
      ],
      filteredDeviceOptions: [], // 根据用户选择的智能设备类别过滤后的设备选项
      // 信号问题选项
      signalProblemOptions: [
        { label: '是', value: 'yes' },
        { label: '否/想了解智能设备', value: 'no' }
      ],
      selectedSignalProblem: null,
      // 解决WiFi意愿选项
      wifiSolveWillOptions: [
        { label: '是', value: 'yes' },
        { label: '不确定', value: 'unsure' },
        { label: '已有设备但信号差', value: 'bad-signal' }
      ],
      selectedWifiSolveWill: null,
      // 宽带信息选项
      broadbandInfoOptions: [
        { label: '中国电信', value: 'telecom' },
        { label: '中国移动', value: 'mobile' },
        { label: '中国联通', value: 'unicom' },
        { label: '暂未安装', value: 'not-installed' }
      ],
      selectedBroadbandInfo: null,
      // 网速信息选项
      networkSpeedOptions: [
        { label: '300Mbps及以下', value: 'below-300' },
        { label: '300-1000Mbps', value: '300-1000' },
        { label: '1000Mbps及以上', value: 'above-1000' },
        { label: '不清楚', value: 'unknown' }
      ],
      selectedNetworkSpeed: null,
      // 组网方式选项
      networkTypeOptions: [
        { label: '光纤到房间（FTTR）', value: 'fttr' },
        { label: 'AC+AP组网', value: 'acap' },
        { label: '路由器', value: 'router' },
        { label: '不清楚', value: 'unknown' }
      ],
      selectedNetworkType: null,
      // 户型选项（已有，调整选项值）
      houseTypeOptions: [
        { id: '2', name: '2室', description: '适合小家庭的智能方案', devices: [] },
        { id: '3', name: '3室', description: '适合三口之家的智能方案', devices: [] },
        { id: '4', name: '4室及以上', description: '适合多成员家庭的智能方案', devices: [] }
      ],
      selectedHouseType: null,
      // 信号弱区域选项
      signalWeakAreaOptions: [
        { label: '客厅', value: 'living-room' },
        { label: '主卧', value: 'master-bedroom' },
        { label: '次卧', value: 'second-bedroom' },
        { label: '卫生间', value: 'bathroom' },
        { label: '阳台', value: 'balcony' }
      ],
      selectedSignalWeakArea: [],
      // 装修状态选项
      decorationStatusOptions: [
        { label: '毛坯', value: 'rough' },
        { label: '已装修', value: 'decorated' },
        { label: '正在装修', value: 'decorating' }
      ],
      selectedDecorationStatus: null,
      // 预埋情况选项
      prewiredOptions: [
        { label: '已预埋网线', value: 'prewired' },
        { label: '未预埋网线', value: 'not-prewired' },
        { label: '计划预埋网线', value: 'planning-prewired' },
        { label: '计划预埋光纤', value: 'planning-fiber' }
      ],
      selectedPrewiredStatus: null,
      // 组网方式选择（已有）
      selectedNetworkSolution: null,
      // 智能设备兴趣选项
      smartDeviceInterestOptions: [
        { label: '是', value: 'yes' },
        { label: '否', value: 'no' }
      ],
      selectedSmartDeviceInterest: null,
      // 智能设备类别选项
      smartDeviceCategoryOptions: [
        { label: '智能门锁', value: 'smart-lock', description: '有了智能门锁，这样出门再也不用担心忘带钥匙啦！' },
        { label: '智能开关', value: 'smart-light', description: '睡前不想下床关灯——喊一声就行，出门忘关灯——手机远程关闭！' },
        { label: '智能遮阳', value: 'smart-curtain', description: '喊一句或者按一下开关就能轻松实现窗帘的开合！' },
        { label: '智能晾衣', value: 'smart-dryer', description: '支持语音升降，按键升降，烘干杀菌！' },
        { label: '全屋控制', value: 'whole-house-control', description: '智能控制您的智慧家庭！' }
      ],
      selectedSmartDeviceCategory: [], // 改为数组支持多选
      // 网络升级需求选项
      networkUpgradeNeedOptions: [
        { label: '需要', value: 'yes' },
        { label: '不需要', value: 'no' }
      ],
      selectedNetworkUpgradeNeed: null,
      contactInfo: {
        name: '',
        phone: '',
        district: '',
        address: ''
      },
      // 联系表单错误
      contactErrors: {
        phone: ''
      },
      // 提交按钮加载状态
      submitLoading: false,
      // 缩放相关属性
      scale: 1,
      // 设备选择页面的当前设备类型索引
      currentDeviceIndex: 0,
      // 设备轮播图当前索引
      currentImageIndex: 0,
      // 轮播图自动切换定时器
      carouselTimer: null,
      // 轮播图滑动相关
      isSliding: false,
      startX: 0,
      currentX: 0,
      slideThreshold: 50,
      // 轮播图放大相关
      showCarouselModal: false,
      currentCarouselImage: null,
      carouselImageScale: 1,
      carouselImageMaxScale: 3,
      carouselImageMinScale: 0.5,
      // 拖动相关
      isDragging: false,
      startX: 0,
      startY: 0,
      translateX: 0,
      translateY: 0,
      // 双指缩放相关
      isPinching: false,
      pinchStartDistance: 0,
      pinchStartScale: 1,
      // 设备库，存储所有设备的公共属性
      deviceLibrary: {},
      // 设备选择状态管理
      selectedDevices: [], // 存储用户选择的设备ID
      needlessDevices: [], // 存储用户选择"我不需要"的设备类型
      // 设备数量管理
      deviceQuantities: {}, // 存储设备ID到数量的映射
      // 户型数据，不再包含设备信息
      houseTypeOptions: [
        { id: '1', name: '一室一厅', description: '适合单身贵族或年轻情侣的小户型智能方案', devices: [] },
        { id: '2', name: '二室一厅', description: '适合小家庭或需要独立办公空间的智能方案', devices: [] },
        { id: '3', name: '三室一厅', description: '适合三口之家或多成员家庭的全面智能方案', devices: [] },
        { id: '4', name: '跃层', description: '适合复式结构住宅的双层智能解决方案', devices: [] },
        { id: '5', name: '别墅', description: '适合大户型别墅的高端全屋智能定制方案', devices: [] }
      ]
    }
  },
  computed: {
    filteredDevices() {
      if (!this.deviceLibrary) {
        return [];
      }
      
      // 转换设备数据，从设备库获取完整信息，只返回用户选择的设备
      return Object.values(this.deviceLibrary)
        .filter(device => this.selectedDevices.includes(device.id))
        .map(device => ({
          ...device,
          quantity: this.deviceQuantities[device.id] || 1
        }));
    },
    groupedDevices() {
      // 按设备类型分组
      const grouped = {};
      
      // 遍历所有设备类型，确保顺序一致
      this.deviceOptions.forEach(option => {
        grouped[option.name] = [];
      });
      
      // 将设备分配到对应的分组
      this.filteredDevices.forEach(device => {
        if (Object.prototype.hasOwnProperty.call(grouped, device.category)) {
          grouped[device.category].push(device);
        }
      });
      
      // 移除空分组
      Object.keys(grouped).forEach(category => {
        if (grouped[category].length === 0) {
          delete grouped[category];
        }
      });
      
      return grouped;
    },
    // 根据当前设备类型过滤设备列表
    filteredDevicesByCategory() {
      if (!this.filteredDeviceOptions[this.currentDeviceIndex] || !this.deviceLibrary) {
        return [];
      }
      
      const currentCategory = this.filteredDeviceOptions[this.currentDeviceIndex].name;
      
      // 直接从设备库获取当前分类的设备
      return Object.values(this.deviceLibrary)
        .filter(device => device.category === currentCategory)
        .map(device => ({
          ...device,
          quantity: this.deviceQuantities[device.id] || 1,
          isSelected: this.selectedDevices.includes(device.id)
        }));
    },
    isContactFormValid() {
      const phoneRegex = /^1[3-9]\d{9}$/;
      return this.contactInfo.name && phoneRegex.test(this.contactInfo.phone);
    },
    // 订单查询表单验证
    isOrderQueryValid() {
      const phoneRegex = /^1[3-9]\d{9}$/;
      return this.orderQueryForm.name && phoneRegex.test(this.orderQueryForm.phone);
    },
    // 当前设备类型
    currentCategory() {
      return this.filteredDeviceOptions[this.currentDeviceIndex]?.name || '';
    },
    // 当前设备类型选项
    currentOption() {
      return this.filteredDeviceOptions[this.currentDeviceIndex] || {};
    },
    // 当前分类是否有已选择的设备
    categoryHasSelectedDevices() {
      const currentCategory = this.currentCategory;
      return Object.values(this.deviceLibrary)
        .some(device => device.category === currentCategory && this.selectedDevices.includes(device.id));
    },
    // 是否可以点击下一步
    canProceedToNext() {
      const currentCategory = this.currentCategory;
      // 如果选择了"我不需要"，可以点击下一步
      if (this.needlessDevices.includes(currentCategory)) {
        return true;
      }
      // 如果选择了至少一个设备，也可以点击下一步
      return this.categoryHasSelectedDevices;
    },
    // 动态计算预估总价范围
    estimatedPriceRange() {
      if (!this.filteredDevices.length) {
        return { min: 0, max: 0 };
      }
      
      // 计算所有设备的最低总价和最高总价，只计算数量大于0的设备
      const { min, max } = this.filteredDevices.reduce((acc, device) => {
        if (device.quantity <= 0) {
          return acc; // 跳过数量为0的设备
        }
        const minPrice = device.minPrice || 0;
        const maxPrice = device.maxPrice || 0;
        const quantity = device.quantity || 1;
        return {
          min: acc.min + (minPrice * quantity),
          max: acc.max + (maxPrice * quantity)
        };
      }, { min: 0, max: 0 });
      
      return { min, max };
    },
    // 规范化区域名称，不在有效列表中的统一为"开渠"
    normalizedArea() {
      if (this.validAreas.includes(this.area)) {
        return this.area;
      }
      return '开渠';
    },
    // 动态获取二维码图片路径
    qrCodePath() {
      // 使用 try-catch 动态加载图片
      try {
        return require(`@/assets/picture/QRCode/${this.normalizedArea}.jpg`);
      } catch (e) {
        // 如果图片不存在，使用默认图片
        return require('@/assets/picture/QRCode/开渠.jpg');
      }
    },
  },
  watch: {
    currentPage() {
      this.updatePageView();
    },
    currentDeviceIndex() {
      // 切换设备类型时重置轮播图
      this.currentImageIndex = 0;
      this.resetCarouselTimer();
    }
  },
  watch: {
    // 监听联系表单手机号变化，更新错误提示
    'contactInfo.phone'(newPhone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (newPhone && !phoneRegex.test(newPhone)) {
        this.contactErrors.phone = '请输入11位有效的手机号码';
      } else {
        this.contactErrors.phone = '';
      }
    },
    // 监听订单查询表单手机号变化，更新错误提示
    'orderQueryForm.phone'(newPhone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (newPhone && !phoneRegex.test(newPhone)) {
        this.orderQueryErrors.phone = '请输入11位有效的手机号码';
      } else {
        this.orderQueryErrors.phone = '';
      }
    }
  },
  mounted() {
    // 从URL获取area参数
    const urlParams = new URLSearchParams(window.location.search);
    this.area = urlParams.get('area') || '';
    // 从URL获取assignee参数
    this.assignee = urlParams.get('assignee') || '';

    this.updatePageView();
    // 模拟数据加载
    this.loadMockData();
    // 启动轮播图定时器
    this.startCarouselTimer();
  },
  beforeUnmount() {
    // 清除定时器
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
    }
  },
  methods: {
    nextPage(targetPage) {
      // 重置缩放比例，特别是在离开户型确认页面时
      this.resetZoom();
      
      // 重置设备选择页面的索引
      if (targetPage === 'devices') {
        this.currentDeviceIndex = 0;
      }
      
      const nextIndex = this.pages.indexOf(targetPage);
      if (nextIndex !== -1) {
        // 记录当前页面作为跳转到ai-qa的来源页面
        if (targetPage === 'ai-qa') {
          this.previousPage = this.currentPage;
        }
        
        // 记录页面访问历史
        // 如果当前页面是路径数组的最后一个，直接添加新页面
        // 如果不是，说明用户可能是从其他路径跳转过来的，需要替换当前页面之后的所有页面
        if (this.currentPage === this.pageHistory[this.pageHistory.length - 1]) {
          this.pageHistory.push(targetPage);
        } else {
          // 找到当前页面在路径数组中的位置，替换之后的所有页面
          const currentIndex = this.pageHistory.indexOf(this.currentPage);
          if (currentIndex !== -1) {
            this.pageHistory = this.pageHistory.slice(0, currentIndex + 1);
            this.pageHistory.push(targetPage);
          }
        }
        
        this.currentPage = targetPage;
        this.updatePageView();
      }
    },
    prevPage() {
      // 从微信二维码页面返回直接回到hero页面，并清除所有用户数据
      if (this.currentPage === 'wechat') {
        this.restart();
      } else if (this.currentPage === 'my-order') {
        // 从my-order页面返回直接回到hero页面
        this.restart();
      } else if (this.currentPage === 'ai-qa') {
        // 从ai-qa页面返回时，根据来源页面决定返回位置
        if (this.previousPage) {
          this.currentPage = this.previousPage;
          // 清空来源页面记录
          this.previousPage = null;
        } else {
          // 默认返回hero页面
          this.restart();
        }
      } else if (this.currentPage === 'order-detail') {
        // 订单详情页面返回查询页面
        this.currentPage = 'my-order';
      } else if (this.currentPage === 'devices') {
        // 设备选择页面返回逻辑
        if (this.currentDeviceIndex > 0) {
          // 如果不是第一个设备类型，返回上一个设备类型
          this.prevDevice();
        } else {
          // 如果是第一个设备类型（智能门锁），返回上一个页面
          this.goToPreviousPage();
        }
      } else {
        // 其他页面根据实际访问路径返回上一页
        this.goToPreviousPage();
      }
      this.updatePageView();
    },
    // 根据实际访问路径返回上一页
    goToPreviousPage() {
      // 确保路径数组中有至少两个页面
      if (this.pageHistory.length > 1) {
        // 移除当前页面
        this.pageHistory.pop();
        // 设置当前页面为路径数组的最后一个页面
        this.currentPage = this.pageHistory[this.pageHistory.length - 1];
      }
    },
    updatePageView() {
      // Toggle Navigation visibility
      const navbar = document.getElementById('nav-header');

      // 只在hero页面隐藏Header，my-order页面也显示Header
      if (this.currentPage === 'hero') {
        navbar?.classList.add('hidden');
      } else {
        navbar?.classList.remove('hidden');
      }

      // Scroll to top with smooth animation
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Update Progress
      this.updateProgressBar();
    },

    // 单独的方法更新进度条，便于复用和维护
    updateProgressBar() {
      const progressBar = document.getElementById('progress-bar');
      if (!progressBar) return;

      // my-order页面是单独分支，不显示进度条
      if (this.currentPage === 'my-order') {
        progressBar.style.width = '0%';
        return;
      }

      // 主流程页面正常显示进度
      const mainPages = [
        'hero', 'signal-problem', 'house-type', 'signal-weak-area',
        'decoration-status', 'prewiring-status', 'broadband-info',
        'network-speed', 'network-type', 'network-solution',
        'smart-device-interest', 'smart-device-category', 'devices',
        'solution', 'contact', 'wechat'
      ];

      const currentIndex = mainPages.indexOf(this.currentPage);
      // 如果当前页面不在主流程中，保持当前进度
      if (currentIndex === -1) return;

      const percent = (currentIndex / (mainPages.length - 1)) * 100;
      progressBar.style.width = `${percent}%`;
    },
    // 选择信号问题
    selectSignalProblem(value) {
      this.selectedSignalProblem = value;
      // 找到对应的中文标签
      const option = this.signalProblemOptions.find(opt => opt.value === value);
      this.userSelections.signalProblem = option ? option.label : value;
    },
    // 选择解决WiFi意愿
    selectWifiSolveWill(value) {
      this.selectedWifiSolveWill = value;
      // 找到对应的中文标签
      const option = this.wifiSolveWillOptions.find(opt => opt.value === value);
      this.userSelections.wifiSolveWill = option ? option.label : value;
    },
    // 选择宽带信息
    selectBroadbandInfo(value) {
      this.selectedBroadbandInfo = value;
      // 找到对应的中文标签
      const option = this.broadbandInfoOptions.find(opt => opt.value === value);
      this.userSelections.broadbandInfo = option ? option.label : value;
    },
    // 选择网速
    selectNetworkSpeed(value) {
      this.selectedNetworkSpeed = value;
      // 找到对应的中文标签
      const option = this.networkSpeedOptions.find(opt => opt.value === value);
      this.userSelections.networkSpeed = option ? option.label : value;
    },
    // 选择组网方式
    selectNetworkType(value) {
      this.selectedNetworkType = value;
      // 找到对应的中文标签
      const option = this.networkTypeOptions.find(opt => opt.value === value);
      this.userSelections.networkType = option ? option.label : value;
    },
    // 选择户型
    selectHouseType(option) {
      this.selectedHouseType = option;
      this.userSelections.houseType = option.name; // 直接存储户型名称
    },
    // 选择信号弱区域
    selectSignalWeakArea(value) {
      // 切换选中状态：如果已选中则移除，否则添加
      const index = this.selectedSignalWeakArea.indexOf(value);
      if (index === -1) {
        this.selectedSignalWeakArea.push(value);
      } else {
        this.selectedSignalWeakArea.splice(index, 1);
      }
      
      const option = this.signalWeakAreaOptions.find(opt => opt.value === value);
      // 将选中的多个区域名称用逗号连接
      const selectedOptions = this.signalWeakAreaOptions.filter(opt => this.selectedSignalWeakArea.includes(opt.value));
      this.userSelections.signalWeakArea = selectedOptions.map(opt => opt.label).join(', ');
    },
    // 选择装修状态
    selectDecorationStatus(value) {
      this.selectedDecorationStatus = value;
      
      const option = this.decorationStatusOptions.find(opt => opt.value === value);
      this.userSelections.decorationStatus = option ? option.label : value;
    },
    // 获取预埋问题文本
    getPrewiringQuestion() {
      switch (this.selectedDecorationStatus) {
        case 'decorated':
          return '您家已装修，是否预埋了网线？';
        case 'decorating':
          return '您家正在装修，是否计划预埋网线/光纤？';
        default:
          return '您家的装修状态，是否涉及预埋网线/光纤？';
      }
    },
    // 获取可用的预埋选项
    getAvailablePrewiredOptions() {
      if (this.selectedDecorationStatus === 'decorated') {
        // 已装修：只显示已预埋或未预埋选项
        return [
          { label: '已预埋网线', value: 'prewired' },
          { label: '未预埋网线', value: 'not-prewired' },
          { label: '我不清楚', value: 'unknown' }
        ];
      } else if (this.selectedDecorationStatus === 'rough') {
        // 毛坯：只显示计划预埋选项
        return [
          { label: '计划预埋网线', value: 'planning-prewired' },
          { label: '计划预埋光纤', value: 'planning-fiber' },
          { label: '我不清楚', value: 'unknown' }
        ];
      } else {
        // 正在装修：显示所有选项
        return [...this.prewiredOptions, { label: '我不清楚', value: 'unknown' }];
      }
    },
    // 选择预埋状态
    selectPrewiredStatus(value) {
      this.selectedPrewiredStatus = value;
      
      const options = this.getAvailablePrewiredOptions();
      const option = options.find(opt => opt.value === value);
      this.userSelections.prewiringStatus = option ? option.label : value;
    },
    // 选择组网方式
    selectNetworkSolution(value) {
      this.selectedNetworkSolution = value;
      // 转换为中文标签
      const solutionMap = {
        'acap': 'AC+AP',
        'fttr': '光纤到房间（FTTR）',
        'none': '我不需要'
      };
      this.userSelections.networkSolution = solutionMap[value] || value;
    },
    // 选择智能设备兴趣
    selectSmartDeviceInterest(value) {
      this.selectedSmartDeviceInterest = value;
      // 找到对应的中文标签
      const option = this.smartDeviceInterestOptions.find(opt => opt.value === value);
      this.userSelections.smartDeviceInterest = option ? option.label : value;
    },
    // 选择智能设备类别（支持多选）
    selectSmartDeviceCategory(value) {
      // 检查值是否已存在，存在则移除，不存在则添加
      const index = this.selectedSmartDeviceCategory.indexOf(value);
      if (index === -1) {
        this.selectedSmartDeviceCategory.push(value);
      } else {
        this.selectedSmartDeviceCategory.splice(index, 1);
      }
      // 转换为中文标签数组
      const categoryLabels = this.selectedSmartDeviceCategory.map(val => {
        const option = this.smartDeviceCategoryOptions.find(opt => opt.value === val);
        return option ? option.label : val;
      });
      this.userSelections.smartDeviceCategory = categoryLabels;
    },
    // 选择网络升级需求
    selectNetworkUpgradeNeed(value) {
      this.selectedNetworkUpgradeNeed = value;
      // 找到对应的中文标签
      const option = this.networkUpgradeNeedOptions.find(opt => opt.value === value);
      this.userSelections.networkUpgradeNeed = option ? option.label : value;
    },
    // 处理信号问题的下一步
    handleSignalProblemNext() {
      if (this.selectedSignalProblem === 'yes') {
        // 如果有信号问题，跳转到收集运营商信息页面
        this.nextPage('house-type');
      } else {
        // 如果没有信号问题或想了解智能设备，跳转到智能设备类别选择页面
        this.nextPage('smart-device-category');
      }
    },
    // 处理智能设备兴趣的下一步
    handleSmartDeviceInterestNext() {
      if (this.selectedSmartDeviceInterest === 'yes') {
        // 如果关注智能设备，跳转到智能设备类别选择页面
        this.nextPage('smart-device-category');
      } else {
        // 如果不关注智能设备，跳转到网络升级需求页面
        this.nextPage('contact');
      }
    },
    // 处理网络升级需求的下一步
    handleNetworkUpgradeNeedNext() {
      if (this.selectedNetworkUpgradeNeed === 'yes') {
        // 如果需要升级网络，回流到宽带信息页面
        this.nextPage('broadband-info');
      } else {
        // 如果不需要升级网络，跳转到设备选择页面
        this.nextPage('devices');
      }
    },
    // 处理智能设备类别选择的下一步
    handleSmartDeviceCategoryNext() {
      // 确保至少选择了一个设备类别
      if (this.selectedSmartDeviceCategory.length === 0) {
        return;
      }
      
      // 定义智能设备类别到设备类型的映射
      const categoryMapping = {
        'smart-lock': '智能门锁',
        'smart-light': '智能开关',
        'smart-curtain': '智能遮阳',
        'smart-dryer': '智能晾衣',
        'whole-house-control': '全屋控制'
      };
      
      // 根据用户选择的智能设备类别过滤deviceOptions
      this.filteredDeviceOptions = this.deviceOptions.filter(option => {
        return this.selectedSmartDeviceCategory.some(category => {
          return categoryMapping[category] === option.name;
        });
      });
      
      // 确保filteredDeviceOptions不为空
      if (this.filteredDeviceOptions.length === 0) {
        // 如果过滤后没有匹配的设备类型，默认使用所有设备类型
        this.filteredDeviceOptions = this.deviceOptions;
      }
      
      // 重置当前设备索引
      this.currentDeviceIndex = 0;
      
      // 跳转到设备选择页面
      this.nextPage('devices'); 
    },
    toggleDeviceSelection(device) {
      const currentCategory = device.category;

      // 如果当前分类选择了"我不需要"，先取消
      if (this.needlessDevices.includes(currentCategory)) {
        this.needlessDevices.splice(this.needlessDevices.indexOf(currentCategory), 1);
      }

      // 检查当前设备类型是否为单选
      const currentOption = this.deviceOptions.find(option => option.name === currentCategory);
      const isAlreadySelected = this.selectedDevices.includes(device.id);

      if (currentOption && currentOption.isSingle) {
        if (isAlreadySelected) {
          // 单选设备再次点击，取消选择
          this.selectedDevices = this.selectedDevices.filter(id => id !== device.id);
        } else {
          // 单选逻辑：移除当前分类下的所有已选设备，只保留当前选中的
          const categoryDevices = Object.values(this.deviceLibrary)
            .filter(d => d.category === currentCategory)
            .map(d => d.id);

          // 移除当前分类下的所有已选设备
          this.selectedDevices = this.selectedDevices.filter(id => !categoryDevices.includes(id));

          // 添加当前设备
          this.selectedDevices.push(device.id);
        }
      } else {
        // 多选逻辑：切换当前设备的选中状态
        const index = this.selectedDevices.indexOf(device.id);
        if (index === -1) {
          this.selectedDevices.push(device.id);
        } else {
          this.selectedDevices.splice(index, 1);
        }
      }
    },
    // 切换"我不需要"选项
    toggleNeedless(category) {
      // 如果是"其他智能"分类且已有选中设备，禁止选择"我不需要"
      if (category === '其他智能' && this.categoryHasSelectedDevices) {
        return;
      }
      
      const index = this.needlessDevices.indexOf(category);
      if (index === -1) {
        // 选择"我不需要"，移除当前分类下的所有已选设备
        const categoryDevices = Object.values(this.deviceLibrary)
          .filter(d => d.category === category)
          .map(d => d.id);
        
        this.selectedDevices = this.selectedDevices.filter(id => !categoryDevices.includes(id));
        this.needlessDevices.push(category);
      } else {
        // 取消"我不需要"
        this.needlessDevices.splice(index, 1);
      }
    },
    getDeviceIconColor(device) {
      const colorMap = {
        '智能开关': '#FACC15', // 黄色
        '智能屏幕': '#3B82F6', // 蓝色
        '智能遮阳': '#22C55E', // 绿色
        '智能网关': '#A855F7', // 紫色
        '智能传感': '#06B6D4', // 青色
        '智能门锁': '#EF4444', // 红色
        '智能暖通': '#F97316', // 橙色
        '智能晾衣': '#EC4899', // 粉红色
        '其他智能': '#6366F1'  // 靛蓝色
      };
      return colorMap[device] || '#FFFFFF';
    },

    // 增加产品数量
    increaseQuantity(device) {
      // 更新设备数量
      this.deviceQuantities[device.id] = (this.deviceQuantities[device.id] || 1) + 1;
    },

    // 减少产品数量
    decreaseQuantity(device) {
      // 更新设备数量，最小为1
      const currentQuantity = this.deviceQuantities[device.id] || 1;
      if (currentQuantity > 1) {
        this.deviceQuantities[device.id] = currentQuantity - 1;
      }
    },

    // 放大
    zoomIn() {
      if (this.scale < 3) {
        this.scale += 0.5;
      }
    },

    // 缩小
    zoomOut() {
      if (this.scale > 1) {
        this.scale -= 0.5;
      }
    },

    // 重置缩放比例
    resetZoom() {
      this.scale = 1;
    },

    restart() {
      this.resetZoom();
      this.currentPage = 'hero';
      // 重置页面访问路径数组
      this.pageHistory = ['hero'];
      this.selectedHouseType = null;
      this.selectedDevices = [];
      this.needlessDevices = [];
      this.deviceQuantities = {};
      // 重置新添加的状态变量
      this.selectedSignalProblem = null;
      this.selectedWifiSolveWill = null;
      this.selectedBroadbandInfo = null;
      this.selectedNetworkSpeed = null;
      this.selectedNetworkType = null;
      this.selectedSignalWeakArea = [];
      this.selectedDecorationStatus = null;
      this.selectedPrewiredStatus = null;
      this.selectedNetworkSolution = null;
      this.selectedSmartDeviceInterest = null;
      this.selectedSmartDeviceCategory = [];
      this.filteredDeviceOptions = [];
      this.selectedNetworkUpgradeNeed = null;
      this.contactInfo = {
        name: '',
        phone: '',
        district: '',
        address: ''
      };
    },
    // 订单查询方法
    async queryOrder() {
      this.orderQueryLoading = true;
      this.orderQueryError = '';
      this.orderQueryResult = null;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/open/order/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerName: this.orderQueryForm.name,
            customerPhone: this.orderQueryForm.phone,
            page: this.pagination.current.toString(),
            size: this.pagination.size.toString()
          })
        });
        
        const data = await response.json();
        
        if (data.code === '200') {
          this.orderQueryResult = data.data;
          this.pagination.total = data.data.total;
          this.pagination.pages = data.data.pages;
          this.pagination.current = data.data.current;
        } else {
          this.orderQueryError = data.message || '查询失败，请重试';
        }
      } catch (error) {
        this.orderQueryError = '网络错误，请重试';
        console.error('订单查询失败:', error);
      } finally {
        this.orderQueryLoading = false;
      }
    },
    // 分页相关方法
    handlePageChange(page) {
      if (page >= 1 && page <= this.pagination.pages) {
        this.pagination.current = page;
        this.queryOrder();
      }
    },
    prevPageItem() {
      if (this.pagination.current > 1) {
        this.pagination.current--;
        this.queryOrder();
      }
    },
    nextPageItem() {
      if (this.pagination.current < this.pagination.pages) {
        this.pagination.current++;
        this.queryOrder();
      }
    },
    resetPagination() {
      this.pagination.current = 1;
      this.orderQueryResult = null;
    },
    // 订单详情相关方法
    async getOrderDetail(orderNo) {
      this.selectedOrderNo = orderNo;
      this.orderDetailLoading = true;
      this.orderDetailError = '';
      this.orderDetail = null;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/open/order/detail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderNo })
        });
        
        const data = await response.json();
        
        if (data.code === '200') {
          this.orderDetail = data.data;
          this.nextPage('order-detail');
        } else {
          this.orderDetailError = data.message || '获取订单详情失败';
        }
      } catch (error) {
        this.orderDetailError = '网络错误，请重试';
        console.error('获取订单详情失败:', error);
      } finally {
        this.orderDetailLoading = false;
      }
    },
    // 返回订单查询页面
    backToOrderQuery() {
      this.nextPage('my-order');
    },
    // AI问答发送消息
    sendAiMessage() {
      if (this.aiInput.trim()) {
        // 添加用户消息
        this.aiMessages.push({
          content: this.aiInput.trim(),
          isUser: true
        });
        
        // 清空输入框
        const inputValue = this.aiInput.trim();
        this.aiInput = '';
        
        // 模拟AI回复
        setTimeout(() => {
          this.aiMessages.push({
            content: `智小家正在处理您的问题："${inputValue}"，请稍候...`,
            isUser: false
          });
        }, 500);
      }
    },
    // 获取AI方案分析
    async getAiAnalysis() {
      // 打包用户选项和设备列表
      let analysisText = '用户选择情况：\n';
      
      // 添加用户选择项
      Object.entries(this.userSelections).forEach(([key, value]) => {
        if (value) {
          // 格式化键名，使其更易读
          const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          analysisText += `${formattedKey}: ${value}\n`;
        }
      });
      
      // 添加设备列表
      analysisText += '\n设备列表：\n';
      this.filteredDevices.forEach(device => {
        analysisText += `${device.category} - ${device.name}\n`;
      });
      
      // 调用AI分析API
      this.aiAnalysis.loading = true;
      this.aiAnalysis.error = null;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/open/ai/chat/device`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: analysisText
          })
        });
        
        const data = await response.json();
        
        if (data.code === '200') {
          this.aiAnalysis.content = data.data.text;
        } else {
          this.aiAnalysis.error = data.message || 'AI分析失败，请稍后重试';
        }
      } catch (error) {
        console.error('AI分析失败:', error);
        this.aiAnalysis.error = 'AI分析失败，请稍后重试';
      } finally {
        this.aiAnalysis.loading = false;
      }
    },
    // 设备选择页面的上一个设备类型
    prevDevice() {
      if (this.currentDeviceIndex > 0) {
        this.currentDeviceIndex--;
        // 滚动到页面顶部，使用平滑动画
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // 智能门锁页面（currentDeviceIndex === 0）时，上一步按钮已禁用，不会执行任何操作
    },
    // 启动轮播图定时器
    startCarouselTimer() {
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
      }
      this.carouselTimer = setInterval(() => {
        this.nextImage();
      }, 3000); // 每3秒切换一次
    },
    // 重置轮播图定时器
    resetCarouselTimer() {
      this.startCarouselTimer();
    },
    // 切换到下一张图片
    nextImage() {
      if (this.deviceOptions[this.currentDeviceIndex]) {
        const totalImages = this.deviceOptions[this.currentDeviceIndex].pictures.length;
        this.currentImageIndex = (this.currentImageIndex + 1) % totalImages;
      }
    },
    // 开始滑动
    startSlide(event) {
      // 停止自动轮播
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
      }
      this.isSliding = true;
      // 获取起始位置
      const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
      this.startX = clientX;
      this.currentX = clientX;
    },
    // 滑动中
    onSlide(event) {
      if (!this.isSliding) return;
      // 更新当前位置
      const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
      this.currentX = clientX;
    },
    // 结束滑动
    endSlide() {
      if (!this.isSliding) return;
      this.isSliding = false;
      // 计算滑动距离和速度
      const diffX = this.currentX - this.startX;
      const absDiffX = Math.abs(diffX);
      
      // 判断是否需要切换图片
      if (absDiffX > this.slideThreshold) {
        // 根据滑动速度调整过渡动画时长
        const animationDuration = Math.max(300, Math.min(500, 1000 - absDiffX * 2));
        
        // 应用动态过渡效果
        const carouselContainer = document.querySelector('.glass-card .flex');
        if (carouselContainer) {
          carouselContainer.style.transition = `transform ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
        }
        
        if (diffX > 0) {
          // 向右滑动，切换到上一张
          if (this.deviceOptions[this.currentDeviceIndex]) {
            const totalImages = this.deviceOptions[this.currentDeviceIndex].pictures.length;
            this.currentImageIndex = (this.currentImageIndex - 1 + totalImages) % totalImages;
          }
        } else {
          // 向左滑动，切换到下一张
          this.nextImage();
        }
        
        // 重置过渡动画时长
        setTimeout(() => {
          const carouselContainer = document.querySelector('.glass-card .flex');
          if (carouselContainer) {
            carouselContainer.style.transition = 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          }
        }, animationDuration);
      } else if (absDiffX > 10) {
        // 滑动距离很小，回弹效果
        const carouselContainer = document.querySelector('.glass-card .flex');
        if (carouselContainer) {
          carouselContainer.style.transition = 'transform 300ms cubic-bezier(0.68, -0.55, 0.27, 1.55)';
        }
        
        setTimeout(() => {
          const carouselContainer = document.querySelector('.glass-card .flex');
          if (carouselContainer) {
            carouselContainer.style.transition = 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          }
        }, 300);
      }
      // 重新启动自动轮播
      this.startCarouselTimer();
    },
    // 设备选择页面的下一个设备类型或生成方案
    nextDevice() {
      if (this.currentDeviceIndex < this.filteredDeviceOptions.length - 1) {
        // 不需要自动选择设备类型，直接进入下一个
        this.currentDeviceIndex++;
        // 滚动到页面顶部，使用平滑动画
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // 最后一个设备类型，生成方案
        if (this.selectedDevices.length === 0) {
          // 设备列表为空，直接跳转到联系表单页面
          this.nextPage('contact');
        } else {
          // 设备列表不为空，跳转到方案页面
          this.nextPage('solution');
        }
      }
    },
    // 显示设备详情弹窗
    showDeviceInfo(device) {
      // 清除可能存在的定时器，防止冲突
      if (this.selectedDeviceTimer) {
        clearTimeout(this.selectedDeviceTimer);
        this.selectedDeviceTimer = null;
      }
      
      this.isClosing = false;
      this.selectedDevice = device;
      this.showDeviceModal = true;
    },
    
    // 关闭设备详情弹窗
    closeDeviceModal() {
      // 先设置关闭状态，触发淡出动画
      this.isClosing = true;
      // 等待动画结束后再关闭弹窗
      setTimeout(() => {
        this.showDeviceModal = false;
        this.isClosing = false;
        // 延迟清空选中设备，确保动画流畅
        if (this.selectedDeviceTimer) {
          clearTimeout(this.selectedDeviceTimer);
        }
        this.selectedDeviceTimer = setTimeout(() => {
          this.selectedDevice = null;
          this.selectedDeviceTimer = null;
        }, 300);
      }, 300);
    },
    
    // 显示轮播图放大弹窗
    openCarouselModal(image) {
      this.currentCarouselImage = image;
      this.showCarouselModal = true;
    },
    
    // 关闭轮播图放大弹窗
    closeCarouselModal() {
      // 先设置关闭状态，触发淡出动画
      this.isClosing = true;
      // 等待动画结束后再关闭弹窗
      setTimeout(() => {
        this.showCarouselModal = false;
        this.isClosing = false;
        // 重置缩放和拖动值
        this.carouselImageScale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.isDragging = false;
        // 延迟清空当前图片，确保动画流畅
        this.currentCarouselImage = null;
      }, 300);
    },
    
    // 放大图片
    zoomIn() {
      if (this.carouselImageScale < this.carouselImageMaxScale) {
        this.carouselImageScale += 0.1;
      }
    },
    
    // 缩小图片
    zoomOut() {
      if (this.carouselImageScale > this.carouselImageMinScale) {
        this.carouselImageScale -= 0.1;
      }
    },
    
    // 重置图片缩放
    resetZoom() {
      this.carouselImageScale = 1;
    },
    
    // 处理滚轮缩放
    handleWheel(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      this.carouselImageScale = Math.max(
        this.carouselImageMinScale,
        Math.min(this.carouselImageMaxScale, this.carouselImageScale + delta)
      );
    },
    
    // 计算两点之间的距离
    calculateDistance(touch1, touch2) {
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    
    // 开始拖动或缩放
    startDrag(event) {
      // 处理双指缩放
      if (event.type.startsWith('touch') && event.touches.length === 2) {
        this.isPinching = true;
        this.pinchStartDistance = this.calculateDistance(event.touches[0], event.touches[1]);
        this.pinchStartScale = this.carouselImageScale;
        return;
      }
      
      // 处理单指拖动
      this.isDragging = true;
      const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
      const clientY = event.type.startsWith('touch') ? event.touches[0].clientY : event.clientY;
      
      this.startX = clientX - this.translateX;
      this.startY = clientY - this.translateY;
      
      // 添加全局事件监听
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchmove', this.onDrag);
      document.addEventListener('touchend', this.endDrag);
    },
    
    // 拖动或缩放中
    onDrag(event) {
      // 处理双指缩放
      if (this.isPinching) {
        if (event.type.startsWith('touch') && event.touches.length === 2) {
          const currentDistance = this.calculateDistance(event.touches[0], event.touches[1]);
          const scaleFactor = currentDistance / this.pinchStartDistance;
          const newScale = this.pinchStartScale * scaleFactor;
          
          this.carouselImageScale = Math.max(
            this.carouselImageMinScale,
            Math.min(this.carouselImageMaxScale, newScale)
          );
        }
        return;
      }
      
      // 处理单指拖动（只有在缩放比例大于1时才允许拖动）
      if (!this.isDragging || this.carouselImageScale <= 1) return;
      
      const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
      const clientY = event.type.startsWith('touch') ? event.touches[0].clientY : event.clientY;
      
      this.translateX = clientX - this.startX;
      this.translateY = clientY - this.startY;
    },
    
    // 结束拖动或缩放
    endDrag() {
      this.isDragging = false;
      this.isPinching = false;
      
      // 移除全局事件监听
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.endDrag);
    },
    
    // 提交订单
    submitOrder() {
      // 设置加载状态
      this.submitLoading = true;
      
      // 收集订单信息
      // userSelections中的houseType已经直接是字符串名称，无需额外处理
      const orderData = {
        name: this.contactInfo.name,
        phone: this.contactInfo.phone,
        district: this.contactInfo.district,
        address: this.contactInfo.address,
        area: this.normalizedArea,
        selections: this.userSelections,
        devices: this.filteredDevices.map(device => ({
          id: device.id,
          name: device.name,
          model: device.model,
          category: device.category,
          quantity: device.quantity,
          minPrice: device.minPrice,
          maxPrice: device.maxPrice
        })),
        assignee: this.assignee || null
      };
      
      // 发送POST请求到API
      fetch(`${API_BASE_URL}/api/open/order/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*'
        },
        body: JSON.stringify(orderData),
        mode: 'cors',
        credentials: 'omit', // Don't send cookies to avoid CORS issues
        redirect: 'follow'
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        // 检查返回的code字段
        if (data.code === "200") {
          // 订单提交成功，跳转到微信页面
          console.log('订单提交成功:', data);
          this.nextPage('wechat');
        } else {
          // 订单提交失败，显示错误信息
          console.error('订单提交失败:', data);
          alert(`订单提交失败: ${data.message || '未知错误'}`);
        }
      })
      .catch(error => {
        // 处理网络错误
        console.error('网络错误:', error);
        alert('网络错误，请检查网络连接后重试');
      })
      .finally(() => {
        // 无论成功失败，都关闭加载状态
        this.submitLoading = false;
      });
    },
    
    loadMockData() {
      // 设备库，存储所有设备的公共属性
      const deviceLibrary = {
        "1": {"id": "1", "category": "智能门锁", "name": "人脸识别门锁", "model": "DZ-F5 ProMax", "minPrice": 1099, "maxPrice": 1299, "shortDescription": "支持人脸+指纹解锁，支持密码解锁，室内有屏幕可直接观看室外情况，C级锁体，摆脱钥匙限制。（70%用户选择）", "picture": require("@/assets/picture/deviceLibrary/智能门锁樱花DZ-F5 ProMax.png"), "detailPicture": require("@/assets/picture/deviceLibrary/智能门锁樱花DZ-F5 ProMax-详情.png")},
        "2": {"id": "2", "category": "智能门锁", "name": "指纹识别门锁", "model": "DZ-H210", "minPrice": 699, "maxPrice": 799, "shortDescription": "支持指纹解锁，密码解锁，C级锁体，摆脱钥匙限制。", "picture": require("@/assets/picture/deviceLibrary/智能门锁樱花DZ-H210.png"), "detailPicture": require("@/assets/picture/deviceLibrary/智能门锁樱花DZ-H210-详情.png")},
        "3": {"id": "3", "category": "智能开关", "name": "轻盈系列灯控面板", "model": "灰/白", "minPrice": 138, "maxPrice": 158, "shortDescription": "可用于控制房间内的灯光，支持远程开灯。", "picture": require("@/assets/picture/deviceLibrary/智能开关.png"), "detailPicture": require("@/assets/picture/deviceLibrary/智能开关-详情.jpg")},
        "4": {"id": "4", "category": "智能遮阳", "name": "电动窗帘", "model": "2.5米-4.5米", "minPrice": 450, "maxPrice": 480, "shortDescription": "支持窗帘的自动开合，回家时自动关闭。", "picture": require("@/assets/picture/deviceLibrary/电动轨道.png"), "detailPicture": require("@/assets/picture/deviceLibrary/电动轨道-详情.png")},
        "5": {"id": "5", "category": "智能晾衣", "name": "电动晾衣架", "model": "白色", "minPrice": 288, "maxPrice": 318, "shortDescription": "支持手动升降，自动升降，可以代替阳台灯光，两侧可伸缩扩展，支持挂孔限位。", "picture": require("@/assets/picture/deviceLibrary/电动晾衣架.png"), "detailPicture": require("@/assets/picture/deviceLibrary/电动晾衣架-详情.jpg")},
        "6": {"id": "6", "category": "全屋控制", "name": "天翼智屏", "model": "标准版", "minPrice": 899, "maxPrice": 929, "shortDescription": "让家的牵挂，随时可见~ 让便捷生活，触手可及~ 这就是有AI~ 新生活~", "picture": require("@/assets/picture/deviceLibrary/天翼智屏.png"), "detailPicture": require("@/assets/picture/deviceLibrary/天翼智屏-详情.png")},
        "7": {"id": "7", "category": "全屋控制", "name": "智能中控屏", "model": "标准版", "minPrice": 899, "maxPrice": 929, "shortDescription": "用于展示时间天气等，同时可以控制房间内的空调，灯光，窗帘等设备，安装在入户玄关位置，不仅方便而且十分美观。", "picture": require("@/assets/picture/deviceLibrary/智能中控屏.png"), "detailPicture": require("@/assets/picture/deviceLibrary/智能中控屏-详情.png")},
        "8": {"id": "8", "category": "全屋控制", "name": "人体存在传感器", "model": "吸顶式", "minPrice": 300, "maxPrice": 358, "shortDescription": "通过使用毫米波雷达来检测人体是否存在，可以探测到非常细微的动态变化，实现人来灯亮人走灯灭。", "picture": require("@/assets/picture/deviceLibrary/人体存在传感器.png"), "detailPicture": require("@/assets/picture/deviceLibrary/人体存在传感器-详情.png")},
        "9": {"id": "9", "category": "全屋控制", "name": "空调控制器", "model": "标准版", "minPrice": 388, "maxPrice": 418, "shortDescription": "用通过控制器将家中空调接入智能，实现手机远程控制，语音控制，亦或是联动门锁和温湿度传感器自动控制空调温度。", "picture": require("@/assets/picture/deviceLibrary/空调控制器.png"), "detailPicture": require("@/assets/picture/deviceLibrary/空调控制器-详情.jpg")}
      };
      
      // 加载用户提供的户型数据，devices只存储设备id、数量和位置
      const mockHouseTypes = [
  {
    "id": "1",
    "name": "一室一厅",
    "description": "适合单身贵族或年轻情侣的小户型智能方案，集舒适、便捷与科技感于一体。该方案通过智能化家居系统，实现灯光、温度、安防等设备的远程控制与自动化管理。无论是日常起居还是休闲娱乐，都能满足现代都市人群对品质生活的追求。",
    "picture": "https://i.ibb.co/QFmW553D/image.png",
    "features": [
      "基础智能开关控制",
      "客厅智能中控屏",
      "客卧室智能窗帘",
      "人体存在传感器联动",
      "智能门锁可视对讲"
    ]
  },
  {
    "id": "2",
    "name": "二室一厅",
    "description": "适合小家庭或需要独立办公空间的智能方案，专为小家庭或需独立办公空间的用户打造，以「分区智控+场景联动」为核心，兼顾家庭共享温馨与个人空间私密。方案通过全屋智能系统，实现客厅、卧室、书房等空间的设备独立控制与跨场景联动。", 
    "picture": "https://i.ibb.co/VcRq0Z3J/image.png",
    "features": [
      "多区域智能开关覆盖",
      "客厅智能中控系统",
      "主要房间智能窗帘",
      "全屋人体感应联动",
      "远程可视对讲门锁"
    ]
  },
  {
    "id": "3",
    "name": "三室一厅",
    "description": "适合三口之家或多成员家庭的全面智能方案，适配三代同堂或二胎家庭的全场景智能方案，以「多空间协同+个性化定制」为核心，满足不同年龄段成员的生活需求。方案针对老人、儿童、成年人的生活习惯，定制专属智能场景。",
    "picture": "https://i.ibb.co/jXjBy4L/image.png",
    "features": [
      "全房屋智能开关覆盖",
      "中央智能控制系统",
      "主要空间智能遮阳",
      "多区域人体感应",
      "安全可视对讲系统"
    ]
  },
  {
    "id": "4",
    "name": "跃层",
    "description": "适合复式结构住宅的双层智能解决方案，为追求空间层次感与生活品质的用户设计，以「上下分层智控+跨层场景联动」为核心，突破楼层限制实现全屋智能协同。方案针对跃层户型的上下空间特点，打造分层独立控制与跨层联动的智能体系。",    
    "picture": "https://i.ibb.co/R4sKZ4dp/image.png",
    "features": [
      "双层智能开关布局",
      "双中控屏分区控制",
      "全屋智能遮阳系统",
      "分层网关部署",
      "多层安全监控"
    ]
  },
  {
    "id": "5",
    "name": "别墅",
    "description": "适合大户型别墅的高端全屋智能定制方案，为高端家庭定制的全维度智能豪宅方案，以「全域智能管控+尊享服务体系」为核心，打造兼具科技感与奢华感的居住体验。方案涵盖庭院、客厅、卧室、影音室、酒窖等全空间，实现设备统一管控与场景化定制。",   
    "picture": "https://i.ibb.co/R4sKZ4dp/image.png",
    "features": [
      "大面积智能开关覆盖",
      "多中控屏分布式控制",
      "全屋智能遮阳系统",
      "多层网关网络部署",
      "全方位安全传感监控"
    ]
  }
];
      
      // 更新户型选项和设备库
      this.houseTypeOptions = mockHouseTypes;
      this.deviceLibrary = deviceLibrary;
    }
  }
}
</script>

<style>
/* 引入外部资源 */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');

/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
  background-color: #000;
  color: #fff;
  overflow: auto;
}

.app-container {
  width: 100vw;
  min-height: 100vh;
  margin: 0 auto;
  position: relative;
  background: #000;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  border: 8px solid #1a1a1a;
  border-radius: 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .app-container {
    border: none;
    border-radius: 0;
    width: 100%;
    height: 100%;
  }
}

.page {
  display: none !important;
  flex-direction: column;
  width: 100%;
  padding: 20px;
  animation: fadeIn 0.5s ease-out forwards;
  overflow-x: hidden;
}

.page.active {
  display: flex !important;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.apple-gradient {
  background: linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%);
}

.glass-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
}

.option-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0.96);
  cursor: pointer;
}

.option-card:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: scale(0.98);
}

.option-card.selected {
  background: #fff;
  color: #000;
  transform: scale(0.96);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.option-card.selected:hover {
  background: #fff;
  transform: scale(0.98);
}

/* 隐藏滚动条 */
::-webkit-scrollbar {
  display: none;
}

/* 设备详情弹窗样式 */
.fixed.inset-0 {
  animation: fadeIn 0.3s ease-out forwards;
}

.fixed.inset-0.fade-out {
  animation: fadeOut 0.3s ease-out forwards;
}

.fixed.inset-0 > div {
  animation: slideUp 0.3s ease-out forwards;
}

.fixed.inset-0 > div.slide-down {
  animation: slideDown 0.3s ease-out forwards;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes slideDown {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* 弹窗内图片样式 */
.fixed.inset-0 img {
  transition: transform 0.3s ease;
}

.fixed.inset-0 img:hover {
  transform: scale(1.02);
}

/* 确保弹窗在所有页面上方 */
.fixed.inset-0 {
  z-index: 9999;
}
</style>
